import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  InputAdornment,
  Avatar,
  Chip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface Contact {
  id: string;
  phoneNumber: string;
  name: string;
  email?: string;
  note?: string;
}

interface ContactFormData {
  phoneNumber: string;
  name: string;
  email: string;
  note: string;
}

interface ContactsProps {
  phoneNumberToAdd?: string;
  onContactAdded?: () => void;
}

const CONTACTS_PER_PAGE = 10;

const Contacts: React.FC<ContactsProps> = ({ phoneNumberToAdd, onContactAdded }) => {
  const { user } = useAuth();
  const userCountryCode = user?.countryCode || '1';
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [formData, setFormData] = useState<ContactFormData>({
    phoneNumber: '',
    name: '',
    email: '',
    note: ''
  });
  const [formErrors, setFormErrors] = useState<Partial<ContactFormData>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadContacts();
  }, []);

  useEffect(() => {
    filterContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, contacts]);

  useEffect(() => {
    if (phoneNumberToAdd) {
      handleOpenDialog(undefined, phoneNumberToAdd);
    }
  }, [phoneNumberToAdd]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/contacts');
      setContacts(response.data.contacts || []);
    } catch (err: any) {
      console.error('Error loading contacts:', err);
      setError(err.response?.data?.error || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    if (!searchQuery.trim()) {
      setFilteredContacts(contacts);
      return;
    }
    const query = searchQuery.toLowerCase();
    const filtered = contacts.filter(contact =>
      contact.name.toLowerCase().includes(query) ||
      contact.phoneNumber.toLowerCase().includes(query) ||
      (contact.email && contact.email.toLowerCase().includes(query)) ||
      (contact.note && contact.note.toLowerCase().includes(query))
    );
    setFilteredContacts(filtered);
    setCurrentPage(1);
  };

  const handleOpenDialog = (contact?: Contact, prefilledPhone?: string) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        phoneNumber: contact.phoneNumber,
        name: contact.name,
        email: contact.email || '',
        note: contact.note || ''
      });
    } else {
      setEditingContact(null);
      setFormData({
        phoneNumber: prefilledPhone || '',
        name: '',
        email: '',
        note: ''
      });
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingContact(null);
    setFormData({ phoneNumber: '', name: '', email: '', note: '' });
    setFormErrors({});
    if (onContactAdded) onContactAdded();
  };

  const validateForm = (): boolean => {
    const errors: Partial<ContactFormData> = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required';
    } else {
      const phoneRegex = /^\+?[1-9]\d{1,14}$/;
      const cleanedNumber = formData.phoneNumber.replace(/[\s\-()]/g, '');
      if (!phoneRegex.test(cleanedNumber)) errors.phoneNumber = 'Invalid phone number format';
    }
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) errors.email = 'Invalid email format';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      setError('');
      const payload = {
        phoneNumber: formData.phoneNumber.replace(/[\s\-()]/g, ''),
        name: formData.name.trim(),
        email: formData.email.trim() || undefined,
        note: formData.note.trim() || undefined
      };
      if (editingContact) {
        await axios.put(`/api/contacts/${editingContact.id}`, payload);
        setSuccess('Contact updated successfully');
      } else {
        await axios.post('/api/contacts', payload);
        setSuccess('Contact created successfully');
      }
      handleCloseDialog();
      loadContacts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error saving contact:', err);
      const errorMsg = err.response?.data?.error || 'Failed to save contact';
      if (errorMsg.includes('already exists') || errorMsg.includes('duplicate')) {
        setFormErrors({ phoneNumber: 'A contact with this phone number already exists' });
      } else {
        setError(errorMsg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contact: Contact) => {
    if (!window.confirm(`Are you sure you want to delete ${contact.name}?`)) return;
    try {
      setError('');
      await axios.delete(`/api/contacts/${contact.id}`);
      setSuccess('Contact deleted successfully');
      loadContacts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error deleting contact:', err);
      const errorMsg = err.response?.data?.error || 'Failed to delete contact';
      if (errorMsg.includes('scheduled message') || errorMsg.includes('referenced')) {
        setError(`Cannot delete ${contact.name}: This contact is referenced by active scheduled messages`);
      } else {
        setError(errorMsg);
      }
    }
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#6c5ce7', '#00b894', '#e17055', '#0984e3', '#fdcb6e', '#e84393', '#00cec9', '#6c5ce7'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  // Pagination
  const totalPages = Math.ceil(filteredContacts.length / CONTACTS_PER_PAGE);
  const startIndex = (currentPage - 1) * CONTACTS_PER_PAGE;
  const paginatedContacts = filteredContacts.slice(startIndex, startIndex + CONTACTS_PER_PAGE);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#2d3436', mb: 0.5, fontSize: { xs: '1.25rem', md: '2.125rem' } }}>Contacts</Typography>
          <Typography variant="body2" sx={{ color: '#999' }}>
            Organize and manage your audience for SMS campaigns
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Search + Add Contact top bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <TextField
          size="small"
          placeholder="Search contacts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: { xs: '100%', sm: 280 }, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
          }}
        />
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: '#6c5ce7',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            px: { xs: 2, md: 3 },
            fontSize: { xs: '0.8rem', md: '0.875rem' },
            whiteSpace: 'nowrap',
            '&:hover': { bgcolor: '#5a4bd1' }
          }}
        >
          Add Contact
        </Button>
      </Box>

      {/* Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', maxWidth: '100%' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>Phone Number</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>Email Address</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>Labels</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedContacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      {searchQuery ? 'No contacts found' : 'No contacts yet. Click "Add Contact" to create one.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedContacts.map((contact) => (
                  <TableRow key={contact.id} sx={{ '&:hover': { bgcolor: '#f9f9fc' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: getAvatarColor(contact.name), width: 38, height: 38, fontSize: 14, fontWeight: 600 }}>
                          {getInitials(contact.name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3436' }}>{contact.name}</Typography>
                          {contact.note && (
                            <Typography variant="caption" sx={{ color: '#999' }}>{contact.note}</Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#555' }}>{contact.phoneNumber}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#555' }}>{contact.email || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      {contact.note && (
                        <Chip
                          label={contact.note}
                          size="small"
                          sx={{
                            bgcolor: '#f0edff',
                            color: '#6c5ce7',
                            fontWeight: 500,
                            fontSize: 11,
                            height: 24,
                            textTransform: 'uppercase',
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog(contact)} sx={{ color: '#999' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(contact)} sx={{ color: '#999', '&:hover': { color: '#e74c3c' } }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Footer */}
        {filteredContacts.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Showing <strong>{paginatedContacts.length}</strong> of <strong>{filteredContacts.length}</strong> contacts
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={<ChevronLeftIcon />}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
                sx={{ textTransform: 'none', color: '#555' }}
              >
                Previous
              </Button>
              <Button
                size="small"
                endIcon={<ChevronRightIcon />}
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
                sx={{ textTransform: 'none', color: '#555' }}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!formErrors.name}
              helperText={formErrors.name}
              required
              fullWidth
            />
            <TextField
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              error={!!formErrors.phoneNumber}
              helperText={formErrors.phoneNumber || `Numbers without + will use +${userCountryCode}`}
              required
              fullWidth
              disabled={false}
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!formErrors.email}
              helperText={formErrors.email}
              fullWidth
            />
            <TextField
              label="Note"
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ bgcolor: '#6c5ce7', '&:hover': { bgcolor: '#5a4bd1' } }}
          >
            {saving ? <CircularProgress size={24} /> : (editingContact ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Contacts;
